/**
 * The save-migration registry (R10's machinery, R11's first real step) —
 * every `(olderSnapshot) → newerSnapshot` rewrite the engine has ever needed,
 * in walking order, keyed by the version each consumes.
 *
 * @remarks
 * Split out of `save-snapshot.ts` at the 500-line cap when D231 added the
 * tenth step. Nothing moved but the text: `walkMigrations` and the opener
 * still live beside the envelope, and both names resolve from there. Every
 * step is pure, recomputes the checksum it ships, and is covered by a REAL
 * older save in the suite.
 *
 * @packageDocumentation
 */

import type { SaveSnapshotEnvelope } from '@orpheus/content-schema';

import { fnv1a64, toCanonicalJson } from './canonical-json.js';

/**
 * One migration step: rewrites a snapshot FROM the named version to the next.
 *
 * @remarks
 * Steps operate on the ENVELOPE, whose `runtimeState` is deliberately
 * `unknown`: an older state's shape is, by definition, not the current
 * schema. Each step is the one place that knows its source shape, and a step
 * that changes the state MUST recompute `checksum` — the integrity seal
 * covers the bytes it ships, not the bytes it received.
 */
export interface SaveMigration {
	/** The `engineSchemaVersion` this step consumes. */
	readonly fromVersion: string;
	/** The pure rewrite; its output's `engineSchemaVersion` names the next step (or the current version). */
	readonly migrate: (olderSnapshot: SaveSnapshotEnvelope) => SaveSnapshotEnvelope;
}

/**
 * `'1'` → `'2'` (R11): `narrative.firedTriggerIds` joined the state — a v1
 * save simply predates every trigger, so the empty list is the true history.
 */
const migrateV1ToV2: SaveMigration = {
	fromVersion: '1',
	migrate: (olderSnapshot) => {
		const olderState = olderSnapshot.runtimeState as { readonly narrative: Record<string, unknown> };
		const migratedState = {
			...olderState,
			narrative: { ...olderState.narrative, firedTriggerIds: []},
		};

		return {
			...olderSnapshot,
			checksum: fnv1a64(toCanonicalJson(migratedState)),
			engineSchemaVersion: '2',
			runtimeState: migratedState,
		};
	},
};

/**
 * `'2'` → `'3'` (R12): the voice/occurrence bookkeeping joined the narrative
 * state, and the reserved-but-never-written `firedVoiceLineIds` array was
 * REPLACED by `voiceLineUsage` (one truth: spoken = count > 0). A v2 save
 * predates every voice line and every tally, so empty records are the true
 * history — and nothing ever wrote the dropped array, so nothing is lost.
 */
const migrateV2ToV3: SaveMigration = {
	fromVersion: '2',
	migrate: (olderSnapshot) => {
		const olderState = olderSnapshot.runtimeState as {
			readonly narrative: Record<string, unknown>;
		};
		const { firedVoiceLineIds: _dropped, ...keptNarrative } = olderState.narrative;
		const migratedState = {
			...olderState,
			narrative: {
				...keptNarrative,
				occurrenceTallies: {},
				voiceLineLastSpokenTurn: {},
				voiceLineUsage: {},
			},
		};

		return {
			...olderSnapshot,
			checksum: fnv1a64(toCanonicalJson(migratedState)),
			engineSchemaVersion: '3',
			runtimeState: migratedState,
		};
	},
};

/**
 * `'3'` → `'4'` (R17/D79): `session.pendingCompletion` became the
 * `pendingQuestion` union, which carries fields a v3 save never recorded
 * (`questionText`, `roomId`, `askedAtInputOrdinal`). A v3 parked question is
 * therefore DROPPED rather than invented: in v3 the question was invisible
 * chrome the player could not see, so a reload that forgets it reproduces
 * v3's own experience — and the alternative was a migration that fabricates
 * where and how a question was asked, which is a lie wearing a version stamp.
 */
const migrateV3ToV4: SaveMigration = {
	fromVersion: '3',
	migrate: (olderSnapshot) => {
		const olderState = olderSnapshot.runtimeState as { readonly session: Record<string, unknown> };
		const { pendingCompletion: _droppedQuestion, ...keptSession } = olderState.session;
		const migratedState = { ...olderState, session: keptSession };

		return {
			...olderSnapshot,
			checksum: fnv1a64(toCanonicalJson(migratedState)),
			engineSchemaVersion: '4',
			runtimeState: migratedState,
		};
	},
};

/**
 * `'4'` → `'5'` (R25): the world gained the acquisition record and the
 * dropped-item locations. A v4 save predates both mechanics, so both start
 * EMPTY — nothing is invented: the player of a v4 session never dropped
 * anything (the verb did not exist), and their acquisition history simply
 * begins at the reload (the map's found-here markers stay silent for items
 * taken before the record existed, which is honest — the record is history,
 * and history that was never written cannot be recovered).
 */
const migrateV4ToV5: SaveMigration = {
	fromVersion: '4',
	migrate: (olderSnapshot) => {
		const olderState = olderSnapshot.runtimeState as { readonly world: Record<string, unknown> };
		const migratedState = {
			...olderState,
			world: { ...olderState.world, acquisitions: [], droppedItemLocations: {}},
		};

		return {
			...olderSnapshot,
			checksum: fnv1a64(toCanonicalJson(migratedState)),
			engineSchemaVersion: '5',
			runtimeState: migratedState,
		};
	},
};

/**
 * `'5'` → `'6'` (D138): the world gained the revealed-exit ledger. A v5 save
 * predates hidden exits, so nothing was ever revealed — and nothing is
 * invented: a pack that gains a `reveal` after the save was taken hides that
 * exit until the player earns it, which is the same thing a new player gets.
 */
const migrateV5ToV6: SaveMigration = {
	fromVersion: '5',
	migrate: (olderSnapshot) => {
		const olderState = olderSnapshot.runtimeState as { readonly world: Record<string, unknown> };
		const migratedState = { ...olderState, world: { ...olderState.world, revealedExitIds: []}};

		return {
			...olderSnapshot,
			checksum: fnv1a64(toCanonicalJson(migratedState)),
			engineSchemaVersion: '6',
			runtimeState: migratedState,
		};
	},
};

/**
 * `'6'` → `'7'` (D141): the randomness state gained `sessionSeed`, the root
 * the session started from. A v6 save's `seed` IS that root — the stream
 * had never been drawn from before D141, so the current state and the
 * origin are the same number — which makes this the rare migration that
 * recovers the new fact exactly rather than starting it empty.
 */
const migrateV6ToV7: SaveMigration = {
	fromVersion: '6',
	migrate: (olderSnapshot) => {
		const olderState = olderSnapshot.runtimeState as { readonly randomness: { readonly seed: number } & Record<string, unknown> };
		const migratedState = {
			...olderState,
			randomness: { ...olderState.randomness, sessionSeed: olderState.randomness.seed },
		};

		return {
			...olderSnapshot,
			checksum: fnv1a64(toCanonicalJson(migratedState)),
			engineSchemaVersion: '7',
			runtimeState: migratedState,
		};
	},
};

/**
 * A section as a migration sees it: a bag of fields, patched without reading them.
 */
type UnknownSection = Readonly<Record<string, unknown>>;

/**
 * Patch one section of a saved state AND the checkpoint's photograph of it.
 *
 * @remarks
 * D142 photographs `world` and `narrative` WHOLE into
 * `progression.checkpoint`, and it arrived at schema `'7'` without a bump
 * (the photograph is optional). So every migration since that adds a field
 * to either section owes the photograph the same field — the v7→v8 and
 * v8→v9 migrations patched only the live state, and a save taken after a
 * checkpoint would have failed validation on load. Found writing v9→v10
 * (D152); all three now go through here.
 *
 * @param state - The older runtime state, untyped
 * @param section - Which photographed section the migration touches
 * @param patch - The field-level change
 * @returns The state with the section patched wherever it appears
 */
const patchPhotographed = (
	state: unknown,
	section: 'narrative' | 'world',
	patch: (older: UnknownSection) => UnknownSection,
): UnknownSection => {
	const older = state as {
		readonly progression: { readonly checkpoint?: UnknownSection } & UnknownSection;
	} & UnknownSection;
	const { checkpoint } = older.progression;

	return {
		...older,
		progression:
			checkpoint === undefined
				? older.progression
				: { ...older.progression, checkpoint: { ...checkpoint, [section]: patch(checkpoint[section] as UnknownSection) }},
		[section]: patch(older[section] as UnknownSection),
	};
};

/**
 * `'7'` → `'8'` (D143): the world gained the worn record. A v7 save predates
 * wearing, so nothing is worn — and nothing is invented: the mask a v7
 * player carried is still carried, and still not on their face.
 */
const migrateV7ToV8: SaveMigration = {
	fromVersion: '7',
	migrate: (olderSnapshot) => {
		const migratedState = patchPhotographed(olderSnapshot.runtimeState, 'world', (world) => ({ ...world, equippedItemIds: {}}));

		return {
			...olderSnapshot,
			checksum: fnv1a64(toCanonicalJson(migratedState)),
			engineSchemaVersion: '8',
			runtimeState: migratedState,
		};
	},
};

/**
 * `'8'` → `'9'` (D151): the one selected ending became a history, because an
 * open ending no longer spends the slot. A v8 save's single value is a
 * one-entry history; none is an empty one — nothing is invented either way.
 */
const migrateV8ToV9: SaveMigration = {
	fromVersion: '8',
	migrate: (olderSnapshot) => {
		const migratedState = patchPhotographed(olderSnapshot.runtimeState, 'narrative', (narrative) => {
			const { selectedEndingId, ...rest } = narrative as { readonly selectedEndingId?: string } & Record<string, unknown>;

			return { ...rest, selectedEndingIds: selectedEndingId === undefined ? [] : [selectedEndingId]};
		});

		return {
			...olderSnapshot,
			checksum: fnv1a64(toCanonicalJson(migratedState)),
			engineSchemaVersion: '9',
			runtimeState: migratedState,
		};
	},
};

/**
 * `'9'` → `'10'` (D152): the world gained the wear record. A v9 save predates
 * durability, so nothing has been spent — the strap a v9 player pulled twice
 * was whole both times, and stays whole.
 */
const migrateV9ToV10: SaveMigration = {
	fromVersion: '9',
	migrate: (olderSnapshot) => {
		const migratedState = patchPhotographed(olderSnapshot.runtimeState, 'world', (world) => ({ ...world, spentUses: {}}));

		return {
			...olderSnapshot,
			checksum: fnv1a64(toCanonicalJson(migratedState)),
			engineSchemaVersion: '10',
			runtimeState: migratedState,
		};
	},
};

/**
 * `'10'` → `'11'` (D231): the narrative state gained the ledger of `once`
 * topics said. A v10 save predates topics, so nothing has been said once —
 * the empty ledger is the true history, in the photograph too.
 */
const migrateV10ToV11: SaveMigration = {
	fromVersion: '10',
	migrate: (olderSnapshot) => {
		const migratedState = patchPhotographed(olderSnapshot.runtimeState, 'narrative', (narrative) => ({ ...narrative, spentTopicIds: []}));

		return {
			...olderSnapshot,
			checksum: fnv1a64(toCanonicalJson(migratedState)),
			engineSchemaVersion: '11',
			runtimeState: migratedState,
		};
	},
};

/**
 * `'11'` → `'12'` (D232): the world gained the record of actors that have
 * moved from where the pack put them. A v11 save predates actors, so nobody
 * has moved — the empty record is the true history, in the photograph too
 * (the checkpoint photographs `world` whole, D142).
 */
const migrateV11ToV12: SaveMigration = {
	fromVersion: '11',
	migrate: (olderSnapshot) => {
		const migratedState = patchPhotographed(olderSnapshot.runtimeState, 'world', (world) => ({ ...world, actorLocations: {}}));

		return {
			...olderSnapshot,
			checksum: fnv1a64(toCanonicalJson(migratedState)),
			engineSchemaVersion: '12',
			runtimeState: migratedState,
		};
	},
};

/**
 * `'12'` → `'13'` (D234): the world gained the fight's two records — what is
 * left of each foe, and which bodies have been struck. A v12 save predates
 * combat, so every foe is whole and nobody has been struck: the empty
 * records are the true history, in the photograph too. The player's health
 * is a delta that a whole player never carries, so a v12 player needs no
 * key at all — the manifest's health is theirs.
 */
const migrateV12ToV13: SaveMigration = {
	fromVersion: '12',
	migrate: (olderSnapshot) => {
		const migratedState = patchPhotographed(olderSnapshot.runtimeState, 'world', (world) => ({ ...world, actorHealth: {}, attackedActorIds: []}));

		return {
			...olderSnapshot,
			checksum: fnv1a64(toCanonicalJson(migratedState)),
			engineSchemaVersion: '13',
			runtimeState: migratedState,
		};
	},
};

/**
 * `'13'` → `'14'` (D235): the narrative state gained the ledger of prompts an
 * actor has asked. A v13 save predates prompts, so nothing has been asked —
 * the empty ledger is the true history, in the photograph too (the checkpoint
 * photographs `narrative` whole, D142, and the restore keeps this field as
 * the player's).
 */
const migrateV13ToV14: SaveMigration = {
	fromVersion: '13',
	migrate: (olderSnapshot) => {
		const migratedState = patchPhotographed(olderSnapshot.runtimeState, 'narrative', (narrative) => ({ ...narrative, askedPromptIds: []}));

		return {
			...olderSnapshot,
			checksum: fnv1a64(toCanonicalJson(migratedState)),
			engineSchemaVersion: '14',
			runtimeState: migratedState,
		};
	},
};

/**
 * `'14'` → `'15'` (D240): the world gained the record of bodies the player
 * has gone through. A v14 save predates a body's pockets, so nobody has been
 * searched — the empty record is the true history, in the photograph too
 * (the checkpoint photographs `world` whole, D142, and its return rewinds a
 * search with the rest of the world).
 */
const migrateV14ToV15: SaveMigration = {
	fromVersion: '14',
	migrate: (olderSnapshot) => {
		const migratedState = patchPhotographed(olderSnapshot.runtimeState, 'world', (world) => ({ ...world, searchedActorIds: []}));

		return {
			...olderSnapshot,
			checksum: fnv1a64(toCanonicalJson(migratedState)),
			engineSchemaVersion: '15',
			runtimeState: migratedState,
		};
	},
};

/**
 * `'15'` → `'16'` (D241): the state gained its view of the Record — the
 * layers the run began with and the layers it has written. A v15 save
 * predates the Record, so its run began with none and has written none: the
 * empty view is the true history. Not photographed — the checkpoint never
 * holds the view — so only the live state gains it.
 */
const migrateV15ToV16: SaveMigration = {
	fromVersion: '15',
	migrate: (olderSnapshot) => {
		const migratedState = { ...(olderSnapshot.runtimeState as UnknownSection), record: { heldAtGenesis: [], writtenThisRun: []}};

		return {
			...olderSnapshot,
			checksum: fnv1a64(toCanonicalJson(migratedState)),
			engineSchemaVersion: '16',
			runtimeState: migratedState,
		};
	},
};

/**
 * `'16'` → `'17'` (D244): the world gained its counters. A v16 save predates
 * every counter, so nothing has moved one: the empty record is the true
 * history (a counter absent from it is at its declared start), in the
 * photograph too — the checkpoint photographs `world` whole (D142), and its
 * return rewinds a world-scoped amount with the rest of the world.
 */
const migrateV16ToV17: SaveMigration = {
	fromVersion: '16',
	migrate: (olderSnapshot) => {
		const migratedState = patchPhotographed(olderSnapshot.runtimeState, 'world', (world) => ({ ...world, counterValues: {}}));

		return {
			...olderSnapshot,
			checksum: fnv1a64(toCanonicalJson(migratedState)),
			engineSchemaVersion: '17',
			runtimeState: migratedState,
		};
	},
};

/** The registry, in walking order — every entry ships with before/after fixtures. */
export const SAVE_MIGRATIONS: readonly SaveMigration[] = [
	migrateV1ToV2,
	migrateV2ToV3,
	migrateV3ToV4,
	migrateV4ToV5,
	migrateV5ToV6,
	migrateV6ToV7,
	migrateV7ToV8,
	migrateV8ToV9,
	migrateV9ToV10,
	migrateV10ToV11,
	migrateV11ToV12,
	migrateV12ToV13,
	migrateV13ToV14,
	migrateV14ToV15,
	migrateV15ToV16,
	migrateV16ToV17,
];
